import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CreateMenuSectionRequest, MenuSection, UpdateMenuSectionRequest, } from 'app/models/menu.model';
import { TranslateService } from '@ngx-translate/core';
import { BaseModalComponent } from 'app/shared/base-modal/base-modal.component';
import { MenuService } from 'app/services/menu.service';
import { IBaseResponse } from 'app/models/baseResponse.model';

@Component({
  selector: 'app-edit-menu-modal',
  templateUrl: './edit-menu-modal.component.html',
  styleUrls: ['./edit-menu-modal.component.scss']
})
export class EditMenuModalComponent extends BaseModalComponent implements OnInit {

  form: UntypedFormGroup;
  downloadProcess = false;

  constructor(
    public dialogRef: MatDialogRef<EditMenuModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogDataType,
    private translate: TranslateService,
    private fb: UntypedFormBuilder,
    private menuService: MenuService,
  ) {
    super(dialogRef);
  }

  get isUpdate(): boolean {
    return !!this.data?.menuSectionId;
  }

  ngOnInit(): void {
    this.initForm(this.data);
  }

  initForm(data: DialogDataType): void {
    this.form = this.fb.group({
      name: [data?.name, [Validators.required]],
      description: [data?.description],
      parentMenuSectionId: [data?.parentMenuSectionId],
      copyFromMenuSectionId: [data?.copyFromMenuSectionId],
      organizationId: [data?.organizationId, Validators.required],
    } as MenuControls) as MenuFormGroup;
  }

  getControl(property: keyof DialogDataType): UntypedFormControl {
    return this.form.controls[property] as UntypedFormControl;
  }

  async submit(): Promise<void> {
    this.downloadProcess = true;
    if (this.form.valid) {
      let response: IBaseResponse<MenuSection>;
      if (this.isUpdate) {
        response = await this.update({id: this.data.menuSectionId, ...this.form.value});
      } else {
        response = await this.create(this.form.value);
      }
      if (response.success){
        this.dialogRef.close();
      }
      else {
        let errorMessage = response.errors[0]?.message;
        let clientMessage =
          (errorMessage && errorMessage.includes('duplicate'))
            ? this.translate.instant('MENU.DUPLICATION_NAME')
            : this.translate.instant('COMMON.OPERATION_FAILED');
        alert(clientMessage);
      }
      this.downloadProcess = false;
    }
  }

  async create(data: CreateMenuSectionRequest): Promise<IBaseResponse<MenuSection>> {
    return await this.menuService.createMenuSection(data);
  }

  async update(data: UpdateMenuSectionRequest): Promise<IBaseResponse<MenuSection>> {
    return await this.menuService.updateMenuSection(data);
  }
}

export interface EditMenuModalComponentData {
  name?: string;
  description?: string;
  menuSectionId?: string,
  parentMenuSectionId?: string,
  copyFromMenuSectionId?: string,
  organizationId: string,
}

type DialogDataType = EditMenuModalComponentData;
type MenuControls = { [key in keyof DialogDataType]: AbstractControl | (string | ((control: AbstractControl) => ValidationErrors)[])[] };
type MenuFormGroup = UntypedFormGroup & { value: DialogDataType, controls: MenuControls };
